import React from 'react';
import { format, differenceInDays, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Calendar,
  ChevronRight,
  Pause,
  Play,
  Plus,
  RefreshCw,
  RotateCcw,
  Trash2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import { Campaign, CampaignStatus } from '../../types';

// Helper to get badge variant for campaign status
function getCampaignStatusBadge(status: CampaignStatus) {
  const statusMap: Record<CampaignStatus, { variant: 'secondary' | 'active' | 'paused' | 'completed'; label: string }> = {
    DRAFT: { variant: 'secondary', label: 'Rascunho' },
    ACTIVE: { variant: 'active', label: 'Ativa' },
    PAUSED: { variant: 'paused', label: 'Pausada' },
    COMPLETED: { variant: 'completed', label: 'Concluida' }
  };
  const config = statusMap[status] || { variant: 'secondary' as const, label: status };
  return <Badge variant={config.variant}>{config.label}</Badge>;
}

// Helper to format date range
function formatDateRange(startDate: string, endDate: string): string {
  const start = parseISO(startDate);
  const end = parseISO(endDate);
  return `${format(start, 'dd/MM/yyyy', { locale: ptBR })} - ${format(end, 'dd/MM/yyyy', { locale: ptBR })}`;
}

// Helper to calculate campaign duration in days
function getCampaignDuration(startDate: string, endDate: string): number {
  return differenceInDays(parseISO(endDate), parseISO(startDate)) + 1;
}

export interface CampaignListViewProps {
  campaigns: Campaign[];
  campaignsLoading: boolean;
  restartSuccessMessage: string | null;
  deleteSuccessMessage: string | null;
  onCreateClick: () => void;
  onRefresh: () => void;
  onCampaignClick: (campaignId: number) => void;
  onStartCampaign: (campaignId: number) => void;
  onPauseCampaign: (campaignId: number) => void;
  onResumeCampaign: (campaignId: number) => void;
  onRestartCampaign: (campaign: Campaign) => void;
  onDeleteCampaign: (campaign: Campaign) => void;
}

export function CampaignListView({
  campaigns,
  campaignsLoading,
  restartSuccessMessage,
  deleteSuccessMessage,
  onCreateClick,
  onRefresh,
  onCampaignClick,
  onStartCampaign,
  onPauseCampaign,
  onResumeCampaign,
  onRestartCampaign,
  onDeleteCampaign
}: CampaignListViewProps) {
  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">
          Gerencie suas campanhas de WhatsApp HSM.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button onClick={onCreateClick}>
            <Plus className="mr-2 h-4 w-4" />
            Nova Campanha
          </Button>
          <Button variant="outline" onClick={onRefresh} disabled={campaignsLoading}>
            <RefreshCw className={cn('h-4 w-4', campaignsLoading && 'animate-spin')} />
          </Button>
        </div>
      </div>

      {/* Campaign Cards */}
      {campaignsLoading ? (
        <div className="flex justify-center py-8">
          <RefreshCw className="h-6 w-6 animate-spin" />
        </div>
      ) : campaigns.length === 0 ? (
        <div className="text-center py-12">
          <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">Nenhuma campanha criada</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Crie sua primeira campanha para comecar a enviar mensagens.
          </p>
          <Button onClick={onCreateClick}>
            <Plus className="mr-2 h-4 w-4" />
            Criar Campanha
          </Button>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {campaigns.map((campaign) => (
            <Card
              key={campaign.id}
              className="cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => onCampaignClick(campaign.id)}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-base">{campaign.name}</CardTitle>
                  {getCampaignStatusBadge(campaign.status)}
                </div>
                <CardDescription className="flex items-center gap-1 text-xs">
                  <Calendar className="h-3 w-3" />
                  {formatDateRange(campaign.startDate, campaign.endDate)}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <div className="text-xs text-muted-foreground">Duracao</div>
                    <div className="font-medium">
                      {getCampaignDuration(campaign.startDate, campaign.endDate)} dias
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Contatos</div>
                    <div className="font-medium">{campaign.contactCount ?? 0}</div>
                  </div>
                </div>
                {campaign.messagesSent !== undefined && campaign.totalMessages !== undefined && (
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Progresso</span>
                      <span>{campaign.messagesSent}/{campaign.totalMessages}</span>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary transition-all"
                        style={{
                          width: `${campaign.totalMessages > 0
                            ? (campaign.messagesSent / campaign.totalMessages) * 100
                            : 0}%`
                        }}
                      />
                    </div>
                  </div>
                )}
                <div className="flex gap-2 pt-2 border-t">
                  {campaign.status === 'DRAFT' && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          onStartCampaign(campaign.id);
                        }}
                      >
                        <Play className="mr-1 h-3 w-3" />
                        Iniciar
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteCampaign(campaign);
                        }}
                        title="Excluir campanha"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                  {campaign.status === 'ACTIVE' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        onPauseCampaign(campaign.id);
                      }}
                    >
                      <Pause className="mr-1 h-3 w-3" />
                      Pausar
                    </Button>
                  )}
                  {campaign.status === 'PAUSED' && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          onResumeCampaign(campaign.id);
                        }}
                      >
                        <Play className="mr-1 h-3 w-3" />
                        Retomar
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteCampaign(campaign);
                        }}
                        title="Excluir campanha"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                  {campaign.status === 'COMPLETED' && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRestartCampaign(campaign);
                        }}
                      >
                        <RotateCcw className="mr-1 h-3 w-3" />
                        Reiniciar
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteCampaign(campaign);
                        }}
                        title="Excluir campanha"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCampaignClick(campaign.id);
                    }}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Success message toast */}
      {restartSuccessMessage && (
        <div className="fixed bottom-4 right-4 z-50 animate-in fade-in slide-in-from-bottom-4">
          <div className="rounded-lg border border-green-200 bg-green-50 dark:bg-green-900/20 dark:border-green-800 px-4 py-3 shadow-lg">
            <p className="text-sm text-green-700 dark:text-green-300">{restartSuccessMessage}</p>
          </div>
        </div>
      )}
      {deleteSuccessMessage && (
        <div className="fixed bottom-4 right-4 z-50 animate-in fade-in slide-in-from-bottom-4">
          <div className="rounded-lg border border-green-200 bg-green-50 dark:bg-green-900/20 dark:border-green-800 px-4 py-3 shadow-lg">
            <p className="text-sm text-green-700 dark:text-green-300">{deleteSuccessMessage}</p>
          </div>
        </div>
      )}
    </div>
  );
}
